import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useEditCompanyDetailsMutation,
  useUploadCompanyPhotoMutation,
} from "../slices/companySlice";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import FieldWarning from "../components/FieldWarning";

const EditCompanyDetailsScreen = ({ companyInfo, editable }) => {
  const { userInfo } = useSelector((state) => state.auth);

  const [companyId, setCompanyId] = useState(companyInfo?._id);
  const [companyCategory, setCompanyCategory] = useState(
    companyInfo?.companyCategory
  );
  const [companyName, setCompanyName] = useState(companyInfo?.companyName);
  const [companyAddress, setCompanyAddress] = useState(
    companyInfo?.companyAddress
  );
  const [companyCity, setCompanyCity] = useState(companyInfo?.companyCity);
  const [pincode, setPincode] = useState(companyInfo?.companyPincode);
  const [state, setState] = useState(companyInfo?.companyState);
  const [companyContact, setCompanyContact] = useState(
    companyInfo?.companyContact
  );

  const [seperatePlatformFee, setSeperatePlatformFee] = useState(
    companyInfo?.separatePlaformFee
  );
  const [platformFee, setPlatformFee] = useState(companyInfo?.platformFee);
  const [addedBy, setAddedBy] = useState(companyInfo?.addedBy);

  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileInvalidError, setFileInvalidError] = useState(false);

  const [companyIdPresent, setCompanyIdPresent] = useState(false);
  const [companyCategoryPresent, setCompanyCategoryPresent] = useState(false);
  const [companyNamePresent, setCompanyNamePresent] = useState(false);
  const [companyAddressPresent, setCompanyAddressPresent] = useState(false);
  const [companyCityPresent, setCompanyCityPresent] = useState(false);
  const [pincodePresent, setPincodePresent] = useState(false);
  const [statePresent, setStatePresent] = useState(false);
  const [companyContactPresent, setCompanyContactPresent] = useState(false);
  const [addedByPresent, setAddedByPresent] = useState(false);
  const [adminNamePresent, setAdminNamePresent] = useState(false);
  const [adminContactPresent, setAdminContactPresent] = useState(false);

  const [seperatePlatformFeePresent, setSeperatePlatformFeePresent] =
    useState(false);
  const [platformFeePresent, setPlatformFeePresent] = useState(false);

  const sepPlatfee = ["Yes", "No"];

  const availableStateNames = [
    "Select State",
    "Andhra Pradesh",
    "Karnataka",
    "Kerala",
    "Maharashtra",
    "Tamil Nadu",
    "Telangana",
  ];

  const navigate = useNavigate();

  const [editCompanyInfo, { isLoading: editCompanyInfoLoading }] =
    useEditCompanyDetailsMutation();
  const [uploadPhoto, { isLoading: isPhotoUploading }] =
    useUploadCompanyPhotoMutation();

  useEffect(() => {
    if (companyId?.length === 8) {
      setCompanyIdPresent(false);
    }

    if (companyName?.trim().length > 0) {
      setCompanyNamePresent(false);
    }

    if (companyAddress?.length > 10) {
      setCompanyAddressPresent(false);
    }

    if (companyCity?.length > 0) {
      setCompanyCityPresent(false);
    }

    if (state != "Select State") {
      setStatePresent(false);
    }

    if (pincode?.length === 6) {
      setPincodePresent(false);
    }

    if (companyContact?.length === 10 && companyContact > 1) {
      setCompanyContactPresent(false);
    }

    if (parseFloat(platformFee) <= 10 && platformFee !== "") {
      setPlatformFeePresent(false);
    }

    if (addedBy?.trim().length > 0) {
      setAddedByPresent(false);
    }
  }, [
    companyId,
    companyName,
    companyAddress,
    companyCity,
    state,
    pincode,
    companyContact,
    platformFee,
    addedBy,
  ]);

  const verifyInputFeilds = async () => {
    let fieldValidationSuccess = true;

    if (companyId?.length !== 8) {
      setCompanyIdPresent(true);
      fieldValidationSuccess = false;
    }

    if (companyName?.trim().length < 1 || companyName?.length > 100) {
      setCompanyNamePresent(true);
      fieldValidationSuccess = false;
    }

    if (companyAddress?.length < 10 || companyAddress?.length > 1000) {
      setCompanyAddressPresent(true);
      fieldValidationSuccess = false;
    }

    if (companyCategory?.trim().length < 1 || companyCategory?.length > 100) {
      setCompanyCategoryPresent(true);
      fieldValidationSuccess = false;
    }

    if (seperatePlatformFee === undefined || seperatePlatformFee === null) {
      setSeperatePlatformFeePresent(true);
      fieldValidationSuccess = false;
    }

    if (companyCity?.length < 1 || companyCity?.length > 100) {
      setCompanyCityPresent(true);
      fieldValidationSuccess = false;
    }

    if (state === "Select State") {
      setStatePresent(true);
      fieldValidationSuccess = false;
    }

    if (pincode?.length !== 6) {
      setPincodePresent(true);
      fieldValidationSuccess = false;
    }

    if (companyContact?.length !== 10) {
      setCompanyContactPresent(true);
      fieldValidationSuccess = false;
    }

    if (parseFloat(platformFee) > 10 || platformFee === "") {
      setPlatformFeePresent(true);
      fieldValidationSuccess = false;
    }
    if (addedBy?.trim().length < 1 || addedBy?.length > 100) {
      setAddedByPresent(true);
      fieldValidationSuccess = false;
    }

    if (!fieldValidationSuccess)
      toast.error(
        `Some of the fields have invalid inputs.
         Please provide valid details`
      );

    return fieldValidationSuccess;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    if (await verifyInputFeilds())
      try {
        const res = await editCompanyInfo({
          _id: companyId,
          separatePlaformFee: seperatePlatformFee,
          platformFee: platformFee,
          companyCategory: companyCategory,
          companyName: companyName,
          companyAddress: companyAddress,
          companyCity: companyCity,
          companyPincode: pincode,
          companyState: state,
          companyContact: companyContact,
          operationStatus: true,
          addedBy: addedBy,
          token: userInfo?.token,
        }).unwrap();
        if (res && file) {
          let formData = new FormData();
          formData.append("image", file);
          formData.append(
            "imageKey",
            companyId + "" + companyId + "" + companyId
          );
          await uploadPhoto({
            payload: formData,
            token: userInfo?.token,
          }).unwrap();
        }
        if (res) {
          toast.success("Company Details Updated Successfully ");
          editable(false);
          navigate("/companyDetails");
        }
      } catch (e) {
        toast.error("Something Went Wrong");
      }
  };

  const handleCancel = () => {
    editable(false);
  };

  const compressImage = async (selectedImage) => {
    const file = selectedImage;
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 280,
          useWebWorker: true,
          initialQuality: 1,
        };

        console.log("Un-Compressed file:", file);
        const compressedFile = await imageCompression(file, options);
        // setFile(compressedFile);
        console.log("first Compressed file:", compressedFile);
        const secondOptions = {
          maxSizeMB: 1, // maximum size in MB
          useWebWorker: true, // use web worker for performance
          initialQuality: 0.9, // initial quality setting
        };

        // Second compression step
        const secondCompressedFile = await imageCompression(
          compressedFile,
          secondOptions
        );
        console.log("Second Compressed file:", secondCompressedFile);
        setFile(secondCompressedFile);

        // // You can now upload the compressed file to your server
        // uploadImage(compressedFile);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e?.target?.files[0];
    const dotIndex = selectedFile?.name?.lastIndexOf(".");
    const fileExtension = selectedFile?.name?.slice(dotIndex + 1).toLowerCase();
    let errorPresent = false;
    if (
      !(
        fileExtension === "jpeg" ||
        fileExtension === "jpg" ||
        fileExtension === "png" ||
        fileExtension === "tiff" ||
        fileExtension === "svg" ||
        fileExtension === "jfif"
      )
    ) {
      errorPresent = true;
      setFileInvalidError(true);
      setFile(null);
    } else if (selectedFile?.size / 1000 > 5120) {
      setFileSizeError(true);
      setFile(null);
    } else if (!errorPresent && selectedFile?.size / 1000 <= 5120) {
      setFileSizeError(false);
      setFileInvalidError(false);
      // setFile(selectedFile);
      await compressImage(selectedFile);
    }
  };
  return (
    <>
      <h6>Edit Company Details</h6>
      <Row
        style={{
          display: "flex",
          margin: "5rem",
          marginLeft: "5rem",
          alignItems: "center",
          justifyContent: "center",
          width: "100",
        }}
      >
        <Row>
          {/* <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Company Id<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                patter="[0-9]"
                value={companyId}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setCompanyId(value);
                }}
                placeholder="Enter Company Id"
              />
              {companyIdPresent ? (
                <FieldWarning text="Please provide valid Company ID" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group> */}
          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Company Name<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                patter="[A-Za-z]+"
                value={companyName}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z ]/g, "");
                  setCompanyName(value);
                }}
                placeholder="Enter Company Name"
              />
              {companyNamePresent ? (
                <FieldWarning text="Company Name should have minimum 1 char and max 100 char" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Company Category<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                patter="[A-Za-z]+"
                value={companyCategory}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");
                  setCompanyCategory(value);
                }}
                placeholder="Enter Company Category"
              />
              {companyCategoryPresent ? (
                <FieldWarning text="Please provide Company Category" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Company Address<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={companyAddress}
                onChange={(e) => {
                  setCompanyAddress(e.target.value);
                }}
                placeholder="Enter Company address"
              />
              {companyAddressPresent ? (
                <FieldWarning text="Address should be atleast 10 chars long" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                City<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                value={companyCity}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");
                  setCompanyCity(value);
                }}
                placeholder="Enter the City"
              />
              {companyCityPresent ? (
                <FieldWarning text="Please enter your city" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Pincode<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                pattern="[0-9]"
                value={pincode}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setPincode(value);
                }}
                placeholder="Enter Pincode"
              />
              {pincodePresent ? (
                <FieldWarning text="Please provide valid 6 digit Pincode" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="my-2" controlId="subscriptionType">
            <Form.Label column="true" sm="3">
              <strong>State</strong>
              <label style={{ color: "red" }}>*</label> :
            </Form.Label>
            <Col sm="6">
              <Form.Select
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="Select State"
              >
                {availableStateNames?.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </Form.Select>
              {statePresent ? <FieldWarning text="Please Select State" /> : ""}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Company Contact<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                patter="[0-9]"
                value={companyContact}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setCompanyContact(value);
                }}
                placeholder="Enter Contact number"
              />
              {companyContactPresent ? (
                <FieldWarning text="contact number should be 10 digits long" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="my-2" controlId="gender">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Seperate Platform Fee ?<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <div className="horizontal-radio-group">
                {sepPlatfee?.map((option) => (
                  <Form.Check
                    key={option}
                    type="radio"
                    label={option}
                    name="platRadio"
                    id={`platRadio-${option}`}
                    value={option}
                    checked={seperatePlatformFee === (option === "Yes")}
                    onChange={(e) =>
                      setSeperatePlatformFee(e.target.value === "Yes")
                    }
                  />
                ))}
              </div>
              {seperatePlatformFeePresent ? (
                <FieldWarning text="Please select your preference" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>
          {/* <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong>
                {" "}
                Platform Fee %<label style={{ color: "red" }}>*</label>
              </strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                pattern="[0-9]"
                value={platformFee}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setPlatformFee(value);
                }}
                placeholder="Enter Platformfee"
              />
              {platformFeePresent ? (
                <FieldWarning text="Platformfee should not be more than 3" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group> */}

          <Form.Group as={Row} className="mb-3">
            <Form.Label column="true" sm="3">
              <strong> Added By</strong>
            </Form.Label>
            <Col sm="6">
              <Form.Control
                type="text"
                patter="[A-Za-z]+"
                value={addedBy}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^A-Za-z]/g, "");
                  setAddedBy(value);
                }}
                placeholder="Enter  Name"
              />
              {addedByPresent ? (
                <FieldWarning text="Please provide your name" />
              ) : (
                ""
              )}
            </Col>
          </Form.Group>

          {/* <Form.Group as={Row} className="my-2" controlId="photo">
            <Form.Label column="true" sm="4">
              upload image :
            </Form.Label>
            <Col sm="6">
              <input
                type="file"
                onChange={handleFileChange}
                style={{ color: "white" }}
              />
              {fileSizeError && (
                <label style={{ color: "red" }}>
                  Image size should be less than 5 MB, Please select different
                  Image.
                </label>
              )}
              {fileInvalidError && (
                <label style={{ color: "red" }}>
                  Only jpeg, jpg, png, tiff and svg formats are supported
                </label>
              )}
            </Col>
          </Form.Group> */}
        </Row>
        <Row className="justify-content-center align-items-center">
          <Col xs="auto">
            <Button onClick={submitHandler}>Submit</Button>
          </Col>
          <Col xs="auto">
            <Button variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Row>
    </>
  );
};

export default EditCompanyDetailsScreen;
